import { useState } from "react";
import { Link } from "react-router-dom";
import { genderMatches } from "../lib/genderUtils";
import type { LeaveBalance, LeaveType } from "../types";

type Props = {
  items: LeaveBalance[];
  employeeGender?: string | null;
  leaveTypesMap?: Record<number, LeaveType>;
};

export function MyLeaveBalancesTable({ items, employeeGender, leaveTypesMap = {} }: Props) {
  const [hideNonApplicable, setHideNonApplicable] = useState(true);

  // Determine applicability for each item
  const annotated = items.map((item) => {
    const leaveType = leaveTypesMap[item.leaveTypeId];
    const applicableGender = leaveType?.applicableGender ?? "ALL";
    const isApplicable = genderMatches(employeeGender, applicableGender);
    return { item, leaveType, applicableGender, isApplicable };
  });

  const visible = hideNonApplicable
    ? annotated.filter((r) => r.isApplicable)
    : annotated;

  const nonApplicableCount = annotated.filter((r) => !r.isApplicable).length;

  return (
    <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-white px-5 py-4">
        <div>
          <h2 className="text-base font-black text-slate-900">My Leave Balances</h2>
          <p className="mt-1 text-sm text-slate-500">
            Allocation, usage, pending days, and remaining balance for each leave type.
          </p>
        </div>
        {nonApplicableCount > 0 && (
          <button
            type="button"
            onClick={() => setHideNonApplicable((v) => !v)}
            className="rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"
          >
            {hideNonApplicable
              ? `Show all (${nonApplicableCount} not applicable)`
              : "Hide non-applicable"}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Leave Type</th>
              <th className="px-4 py-3">Year</th>
              <th className="px-4 py-3">Opening</th>
              <th className="px-4 py-3">Entitled</th>
              <th className="px-4 py-3">B/F</th>
              <th className="px-4 py-3">Earned</th>
              <th className="px-4 py-3">Pending</th>
              <th className="px-4 py-3">Used</th>
              <th className="px-4 py-3">Forfeited</th>
              <th className="px-4 py-3">Remaining</th>
              <th className="px-4 py-3">Eligibility</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {visible.map(({ item, applicableGender, isApplicable }) => {
              const isGenderRestricted = !isApplicable;
              const rowClass = isGenderRestricted ? "bg-slate-50 opacity-70" : "hover:bg-slate-50";

              return (
                <tr key={item.id} className={rowClass}>
                  <td className="px-4 py-3">
                    <div className={`font-black ${isGenderRestricted ? "text-slate-400" : "text-slate-900"}`}>
                      {item.leaveTypeName}
                    </div>
                    <div className="text-xs text-slate-400">{item.leaveTypeCode}</div>
                    {applicableGender !== "ALL" && (
                      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-bold ${
                        applicableGender === "FEMALE"
                          ? "bg-pink-100 text-pink-700"
                          : "bg-blue-100 text-blue-700"
                      }`}>
                        {applicableGender === "FEMALE" ? "♀ Female only" : "♂ Male only"}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3 text-slate-600">{item.leaveYear}</td>
                  <td className="px-4 py-3">{formatDays(item.openingBalance)}</td>
                  <td className="px-4 py-3">{formatDays(item.entitledDays)}</td>
                  <td className="px-4 py-3">{formatDays(item.broughtForwardDays)}</td>
                  <td className="px-4 py-3">{formatDays(item.earnedDays)}</td>

                  <td className="px-4 py-3">
                    <NumberBadge value={item.pendingDays} tone="amber" dimmed={isGenderRestricted} />
                  </td>

                  <td className="px-4 py-3">
                    <NumberBadge value={item.usedDays} tone="slate" dimmed={isGenderRestricted} />
                  </td>

                  <td className="px-4 py-3">
                    <NumberBadge value={item.forfeitedDays} tone="red" dimmed={isGenderRestricted} />
                  </td>

                  <td className="px-4 py-3">
                    {isGenderRestricted ? (
                      <span className="inline-flex min-w-14 justify-center rounded-xl bg-slate-100 px-3 py-2 text-sm font-black text-slate-400">
                        {formatDays(item.remainingDays)}
                      </span>
                    ) : (
                      <RemainingBadge entitled={item.entitledDays} remaining={item.remainingDays} />
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {isGenderRestricted ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">
                        Cannot apply
                      </span>
                    ) : (
                      <Link
                        to="/hr/leave/requests/new"
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-200"
                      >
                        Apply
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}

            {visible.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                  {items.length === 0
                    ? "No leave balances found for the selected year."
                    : "No applicable leave types for your gender. Toggle above to view all."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RemainingBadge({
  entitled,
  remaining,
}: {
  entitled?: number | null;
  remaining?: number | null;
}) {
  return (
    <span
      className={`inline-flex min-w-16 justify-center rounded-xl px-3 py-2 text-sm font-black ${balanceClass(
        entitled,
        remaining,
      )}`}
    >
      {formatDays(remaining)}
    </span>
  );
}

function NumberBadge({
  value,
  tone,
  dimmed,
}: {
  value?: number | null;
  tone: "emerald" | "amber" | "red" | "slate";
  dimmed?: boolean;
}) {
  if (dimmed) {
    return (
      <span className="inline-flex min-w-14 justify-center rounded-xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-400">
        {formatDays(value)}
      </span>
    );
  }

  const classes = {
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <span
      className={`inline-flex min-w-14 justify-center rounded-xl px-3 py-2 text-sm font-bold ${classes[tone]}`}
    >
      {formatDays(value)}
    </span>
  );
}

function balanceClass(entitled?: number | null, remaining?: number | null) {
  const entitledDays = Number(entitled ?? 0);
  const remainingDays = Number(remaining ?? 0);

  if (remainingDays <= 0) return "bg-red-100 text-red-800";
  if (entitledDays > 0 && remainingDays < entitledDays) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

function formatDays(value?: number | null) {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}
